import React from "react";
import {
  CheckboxGrid,
  ExclusiveChoiceGrid,
  labelClass,
  fieldClass,
  textareaClass,
  checkboxClass,
} from "./clinicalForms/formLayout";
import { RowDateField } from "./MultiRowFields";
import {
  TIME_OPTIONS_15,
  normalizeTimeOption,
  time12ToMinutes,
} from "./clinicalForms/nurseNotes/nurseVisitCommon";
import {
  OTHER_OPTION,
  PETS_OPTION,
  DAYS_OF_SERVICE,
  HOME_TYPE_OPTIONS,
  SAFETY_OPTIONS,
  normalizeScheduleDate,
  normalizeScheduleTime,
} from "../assessmentForm.shared";

const compactInputClass = `${fieldClass} h-8 text-sm`;
const compactTextareaClass = `${textareaClass} min-h-[56px] text-sm`;
const bodyPad = (printMode) =>
  printMode ? "px-3 py-1.5 space-y-2" : "p-4 space-y-3";
const gridPad = (printMode) => (printMode ? "px-3 py-1" : "p-4");

/** Split options into row pairs; "Other" gets its own full-width row. */
const partitionOptions = (options) => {
  const list = [...options];
  const otherIdx = list.indexOf(OTHER_OPTION);
  const other = otherIdx >= 0 ? list.splice(otherIdx, 1)[0] : null;
  const pairs = [];
  for (let i = 0; i < list.length; i += 2) {
    pairs.push([list[i], list[i + 1] ?? null]);
  }
  return { pairs, other };
};

const matrixRowClass =
  "inline-flex items-center gap-2 min-h-[32px] w-full text-[12px] text-slate-700 rounded px-1.5 py-1";
const matrixCheckedClass = "bg-indigo-50/70 text-indigo-900 font-medium";

export const ViewField = ({ label, value, className = "" }) => (
  <div className={`min-w-0 py-0.5 ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-slate-900 font-medium break-words leading-snug">
      {value != null && String(value).trim() !== "" ? value : "—"}
    </div>
  </div>
);

const ViewFieldGrid = ({ children, printMode = false }) => (
  <div
    className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-3 ${
      printMode ? "gap-y-2" : gridPad(printMode)
    }`}
  >
    {children}
  </div>
);

const NumberField = ({ label, value, onChange, className = "w-full" }) => (
  <div className={className}>
    <label className={labelClass}>{label}</label>
    <input
      type="number"
      min="0"
      step="0.25"
      className={compactInputClass}
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
    />
  </div>
);

const AssessmentDateField = ({ label, value, onChange }) => (
  <RowDateField
    label={label}
    value={normalizeScheduleDate(value)}
    onChange={onChange}
  />
);

const AssessmentTimeField = ({ label, value, onChange, minTime = "" }) => {
  const normalized = normalizeScheduleTime(value);
  const minMinutes = minTime ? time12ToMinutes(minTime) : null;
  const baseOptions =
    minMinutes == null
      ? TIME_OPTIONS_15
      : TIME_OPTIONS_15.filter((opt) => {
          const mins = time12ToMinutes(opt);
          return mins != null && mins > minMinutes;
        });
  const options =
    normalized && !baseOptions.includes(normalized)
      ? [normalized, ...baseOptions]
      : baseOptions;

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select
        className={compactInputClass}
        value={normalized}
        onChange={(e) => onChange?.(e.target.value)}
        aria-label={label}
      >
        <option value="">Select time…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
};

/**
 * Compact 2-column row matrix — used for Behavior/Cognitive, Mobility/Continence.
 */
export const CompactCheckboxMatrix = ({
  options,
  selected,
  canEdit = false,
  onToggle,
  printMode = false,
}) => {
  const sel = Array.isArray(selected) ? selected : [];
  const { pairs, other } = partitionOptions(options);
  const rowClass = printMode
    ? "assessment-print-matrix-row inline-flex items-center gap-1.5 w-full text-[11px] text-slate-700 rounded px-1 py-0.5"
    : matrixRowClass;

  const renderCell = (opt) => {
    if (!opt) {
      return printMode ? null : <div className="min-h-[32px]" aria-hidden />;
    }
    const checked = sel.includes(opt);
    const body = (
      <>
        <input
          type="checkbox"
          className={`${checkboxClass} mt-0 shrink-0`}
          checked={checked}
          disabled={!canEdit}
          readOnly={!canEdit}
          onChange={canEdit ? () => onToggle?.(opt) : undefined}
          aria-label={opt}
        />
        <span className="leading-snug break-words">{opt}</span>
      </>
    );
    if (canEdit) {
      return (
        <label
          className={`${rowClass} cursor-pointer hover:bg-slate-50 ${
            checked ? matrixCheckedClass : ""
          }`}
        >
          {body}
        </label>
      );
    }
    return (
      <div
        className={`${rowClass} ${checked ? matrixCheckedClass : "text-slate-600"}`}
      >
        {body}
      </div>
    );
  };

  return (
    <div className={printMode ? "space-y-0" : "space-y-0.5"}>
      {pairs.map((pair, idx) => (
        <div
          key={`row-${idx}`}
          className={`grid grid-cols-2 gap-x-2 items-start ${
            printMode ? "gap-y-0" : "gap-y-0.5"
          }`}
        >
          {renderCell(pair[0])}
          {renderCell(pair[1])}
        </div>
      ))}
      {other ? (
        <div className="pt-0.5 border-t border-slate-100 mt-1">
          {renderCell(other)}
        </div>
      ) : null}
    </div>
  );
};

/** Unified compact checkbox panel — same layout in view and edit. */
export const CompactCheckboxSection = ({
  options,
  selected,
  otherDetails,
  canEdit = false,
  onToggle,
  onOtherChange,
  otherLabel = "Enter details",
  otherLabelView = "Other details",
  printMode = false,
}) => {
  const showOther = (selected || []).includes(OTHER_OPTION);
  return (
    <div className={printMode ? "space-y-1" : "space-y-2"}>
      <CompactCheckboxMatrix
        options={options}
        selected={selected}
        canEdit={canEdit}
        onToggle={onToggle}
        printMode={printMode}
      />
      {showOther ? (
        canEdit ? (
          <div>
            <label className={labelClass}>{otherLabel}</label>
            <input
              type="text"
              className={compactInputClass}
              value={otherDetails || ""}
              onChange={(e) => onOtherChange?.(e.target.value)}
              placeholder="Specify…"
            />
          </div>
        ) : (
          <ViewField label={otherLabelView} value={otherDetails} />
        )
      ) : null}
    </div>
  );
};

/** @deprecated use CompactCheckboxSection */
export const CompactCheckboxGroup = CompactCheckboxSection;
/** @deprecated use CompactCheckboxSection */
export const CompactViewCheckboxGroup = (props) => (
  <CompactCheckboxSection {...props} canEdit={false} />
);

/** Unified checkbox section — same grid in view and edit (CheckboxGrid). */
export const CheckboxSection = ({
  options,
  selected,
  otherDetails,
  canEdit = false,
  onToggle,
  onOtherChange,
  columns = 4,
  otherLabel = "Enter details",
  otherLabelView = "Other details",
  printMode = false,
  showInlineOther = true,
}) => {
  const showOther =
    showInlineOther && (selected || []).includes(OTHER_OPTION);
  return (
    <div className={bodyPad(printMode)}>
      <CheckboxGrid
        options={options}
        values={selected}
        canEdit={canEdit}
        columns={columns}
        onToggle={canEdit ? onToggle : undefined}
      />
      {showOther ? (
        canEdit ? (
          <div className="max-w-md pt-1 px-1">
            <label className={labelClass}>{otherLabel}</label>
            <input
              type="text"
              className={compactInputClass}
              value={otherDetails || ""}
              onChange={(e) => onOtherChange?.(e.target.value)}
              placeholder="Specify…"
            />
          </div>
        ) : (
          <div className="px-1">
            <ViewField label={otherLabelView} value={otherDetails} />
          </div>
        )
      ) : null}
    </div>
  );
};

/** @deprecated use CheckboxSection */
export const ViewCheckboxGrid = (props) => (
  <CheckboxSection {...props} canEdit={false} />
);
/** @deprecated use CheckboxSection */
export const EditCheckboxGroup = (props) => (
  <CheckboxSection {...props} canEdit />
);

const DaysOfServiceField = ({ days, canEdit, onToggleDay, printMode = false }) => (
  <div className={`${printMode ? "px-3 pb-1" : "px-4 pb-2"}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
      Days of Service
    </div>
    <div className="flex flex-wrap gap-1.5">
      {DAYS_OF_SERVICE.map((day) => {
        const checked = (days || []).includes(day);
        const chipClass = `inline-flex items-center gap-1.5 text-xs font-medium rounded-md border px-2.5 py-1.5 ${
          checked
            ? "border-indigo-300 bg-indigo-50/60 text-indigo-800"
            : "border-slate-200 bg-white text-slate-600"
        }`;
        if (canEdit) {
          return (
            <label key={day} className={`${chipClass} cursor-pointer text-slate-700`}>
              <input
                type="checkbox"
                className={`${checkboxClass} mt-0`}
                checked={checked}
                onChange={() => onToggleDay?.(day)}
              />
              {day}
            </label>
          );
        }
        return (
          <div key={day} className={chipClass}>
            <input
              type="checkbox"
              className={`${checkboxClass} mt-0`}
              checked={checked}
              disabled
              readOnly
            />
            {day}
          </div>
        );
      })}
    </div>
  </div>
);

/** Schedule of care — identical layout in view and edit. */
export const ScheduleOfCareFields = ({
  data,
  canEdit,
  onChange,
  printMode = false,
}) => {
  const set = (key, value) => onChange?.({ ...data, [key]: value });

  const toggleDay = (day) => {
    const days = data.daysOfService || [];
    set(
      "daysOfService",
      days.includes(day) ? days.filter((d) => d !== day) : [...days, day],
    );
  };

  return (
    <div className={bodyPad(printMode)}>
      {canEdit ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-3 px-4">
          <AssessmentDateField
            label="Start Date"
            value={data.startDate}
            onChange={(v) => set("startDate", v)}
          />
          <AssessmentDateField
            label="End Date"
            value={data.endDate}
            onChange={(v) => set("endDate", v)}
          />
          <AssessmentTimeField
            label="Start Time"
            value={data.startTime}
            onChange={(v) => set("startTime", v)}
          />
          <AssessmentTimeField
            label="End Time"
            value={data.endTime}
            minTime={data.startTime}
            onChange={(v) => set("endTime", v)}
          />
          <NumberField
            label="Total Hours / Day"
            value={data.totalHoursPerDay}
            onChange={(v) => set("totalHoursPerDay", v)}
          />
          <NumberField
            label="Total Hours / Week"
            value={data.totalHoursPerWeek}
            onChange={(v) => set("totalHoursPerWeek", v)}
          />
        </div>
      ) : (
        <ViewFieldGrid printMode={printMode}>
          <ViewField
            label="Start Date"
            value={normalizeScheduleDate(data.startDate)}
          />
          <ViewField
            label="End Date"
            value={normalizeScheduleDate(data.endDate)}
          />
          <ViewField
            label="Start Time"
            value={normalizeScheduleTime(data.startTime)}
          />
          <ViewField
            label="End Time"
            value={normalizeScheduleTime(data.endTime)}
          />
          <ViewField label="Total Hours / Day" value={data.totalHoursPerDay} />
          <ViewField label="Total Hours / Week" value={data.totalHoursPerWeek} />
        </ViewFieldGrid>
      )}
      <DaysOfServiceField
        days={data.daysOfService}
        canEdit={canEdit}
        onToggleDay={toggleDay}
        printMode={printMode}
      />
    </div>
  );
};

export const DualCheckboxColumns = ({
  leftTitle,
  rightTitle,
  left,
  right,
  canEdit,
  printMode = false,
}) => (
  <div
    className={`assessment-dual-columns grid grid-cols-1 lg:grid-cols-2 gap-3 ${
      printMode ? "items-start gap-2" : "items-stretch"
    } ${gridPad(printMode)}`}
  >
    <DualPanel title={leftTitle} printMode={printMode}>
      <CompactCheckboxSection
        options={left.options}
        selected={left.selected}
        otherDetails={left.otherDetails}
        canEdit={canEdit}
        onToggle={left.onToggle}
        onOtherChange={left.onOtherChange}
        printMode={printMode}
      />
    </DualPanel>
    <DualPanel title={rightTitle} printMode={printMode}>
      <CompactCheckboxSection
        options={right.options}
        selected={right.selected}
        otherDetails={right.otherDetails}
        canEdit={canEdit}
        onToggle={right.onToggle}
        onOtherChange={right.onOtherChange}
        printMode={printMode}
      />
    </DualPanel>
  </div>
);

const DualPanel = ({ title, children, printMode = false }) => (
  <div
    className={`assessment-dual-panel flex flex-col rounded-lg border border-indigo-100 bg-white overflow-hidden ${
      printMode ? "min-h-0" : "h-full min-h-[168px]"
    }`}
  >
    <div className="px-3 py-1.5 bg-indigo-50/90 border-b border-indigo-100 shrink-0 print:px-2 print:py-0.5">
      <h4 className="text-[10px] font-semibold uppercase tracking-wide text-indigo-800 print:text-[9px]">
        {title}
      </h4>
    </div>
    <div className={printMode ? "p-1" : "flex-1 p-3"}>{children}</div>
  </div>
);

/** Home assessment — same layout in view and edit. */
export const HomeAssessmentFields = ({
  data,
  canEdit,
  onChange,
  printMode = false,
}) => {
  const set = (key, value) => onChange?.({ ...data, [key]: value });

  return (
    <div className={bodyPad(printMode)}>
      <div>
        <label className={labelClass}>Type of Home</label>
        <ExclusiveChoiceGrid
          name={canEdit ? "homeType" : "homeTypeView"}
          options={[...HOME_TYPE_OPTIONS]}
          value={data.homeType || ""}
          canEdit={canEdit}
          columns={4}
          onChange={canEdit ? (v) => set("homeType", v) : undefined}
        />
      </div>
      {data.homeType === "Apartment" ? (
        canEdit ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-xl px-1">
            <NumberField
              label="Floor"
              className="w-24"
              value={data.apartmentFloor}
              onChange={(v) => set("apartmentFloor", v)}
            />
            <div className="col-span-2">
              <label className={labelClass}>Elevator Accessible</label>
              <ExclusiveChoiceGrid
                name="elevatorAccessible"
                options={["Yes", "No"]}
                value={data.elevatorAccessible || ""}
                canEdit
                columns={2}
                onChange={(v) => set("elevatorAccessible", v)}
              />
            </div>
          </div>
        ) : (
          <ViewFieldGrid printMode={printMode}>
            <ViewField label="Floor" value={data.apartmentFloor} />
            <ViewField label="Elevator Accessible" value={data.elevatorAccessible} />
          </ViewFieldGrid>
        )
      ) : null}
      {data.homeType === "House" ? (
        canEdit ? (
          <div className="max-w-md px-1">
            <label className={labelClass}>Floors</label>
            <ExclusiveChoiceGrid
              name="houseFloors"
              options={["1 Floor", "2+ Floors"]}
              value={data.houseFloors || ""}
              canEdit
              columns={2}
              onChange={(v) => set("houseFloors", v)}
            />
          </div>
        ) : (
          <ViewField label="Floors" value={data.houseFloors} className="px-4" />
        )
      ) : null}
      {data.homeType === "Other" ? (
        canEdit ? (
          <div className="max-w-md px-1">
            <label className={labelClass}>Specify</label>
            <input
              type="text"
              className={compactInputClass}
              value={data.otherSpecify || ""}
              onChange={(e) => set("otherSpecify", e.target.value)}
              placeholder="Describe home type…"
            />
          </div>
        ) : (
          <ViewField label="Specify" value={data.otherSpecify} className="px-4" />
        )
      ) : null}
    </div>
  );
};

/** Safety assessment — same CheckboxGrid layout in view and edit. */
export const SafetyAssessmentFields = ({
  data,
  canEdit,
  onToggle,
  onChange,
  printMode = false,
}) => {
  const showPets = (data.selected || []).includes(PETS_OPTION);
  const showOther = (data.selected || []).includes(OTHER_OPTION);
  const detailPad = printMode ? "px-3 pb-1" : "px-4 pb-2";

  return (
    <div className={printMode ? "space-y-0" : "space-y-1"}>
      <div className={bodyPad(printMode)}>
        <CheckboxGrid
          options={[...SAFETY_OPTIONS]}
          values={data.selected}
          canEdit={canEdit}
          columns={3}
          onToggle={canEdit ? onToggle : undefined}
        />
      </div>
      {showPets ? (
        <div className={detailPad}>
          {canEdit ? (
            <>
              <label className={labelClass}>
                If yes, please note number and type of pet/s
              </label>
              <textarea
                className={compactTextareaClass}
                rows={2}
                value={data.petsDetails || ""}
                onChange={(e) =>
                  onChange?.({ ...data, petsDetails: e.target.value })
                }
              />
            </>
          ) : (
            <ViewField
              label="If yes, please note number and type of pet/s"
              value={data.petsDetails}
            />
          )}
        </div>
      ) : null}
      {showOther ? (
        <div className={detailPad}>
          {canEdit ? (
            <>
              <label className={labelClass}>Enter details</label>
              <input
                type="text"
                className={compactInputClass}
                value={data.otherDetails || ""}
                onChange={(e) =>
                  onChange?.({ ...data, otherDetails: e.target.value })
                }
              />
            </>
          ) : (
            <ViewField label="Other details" value={data.otherDetails} />
          )}
        </div>
      ) : null}
    </div>
  );
};
